import { useAuthStoreV1 } from "@/stores/authStore_v1";
import type { GitBranch, GitRepo } from "@/services/git/types";
import { useGitConnection } from "./useGitConnection";
import { useGitSelectedRepo } from "./useGitSelectedRepo";
import { useDefaultGitBranch } from "./useDefaultGitBranch";

/**
 * The org's connected GitHub repo + the user's active (default) branch,
 * resolved the one way this repo already does it: a live connection, its
 * selected repo, and the default branch (`DeployLayout.tsx`'s `GitHubPanel`,
 * pre-L-21, inlined this same three-hook chain — `useGitConnection` gates
 * `useGitSelectedRepo`, and `commitStagedFiles`/`pushSalesforceToGit` always
 * read `repo.full_name` + `branch.name` off exactly these two queries).
 *
 * Pulled out to a shared hook so a second consumer — the changeset
 * save→stage→commit chain (L-21, `SaveBar.tsx`) — does not grow its own copy
 * of the resolution (rule 21: the seam is "what repo/branch does this org
 * commit to", and it now has exactly one owner). `GitHubPanel` was refactored
 * onto this hook in the same change; nothing about its resolution logic
 * changed, only where it lives.
 *
 * Also carries `orgId`, since both this hook's own queries and every
 * `services/git/api` call need it, and needing it is what "resolving a git
 * target" already implies for a caller.
 */
export interface ResolvedGitTarget {
  orgId: string | null;
  isConnected: boolean;
  repo: GitRepo | null;
  branch: GitBranch | null;
  /** Convenience: `repo?.full_name ?? null`. */
  repoFullName: string | null;
  /** Convenience: `branch?.name ?? null`. */
  branchName: string | null;
  isLoading: boolean;
}

export function useResolvedGitTarget(): ResolvedGitTarget {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id) ?? null;
  const { data: connection, isLoading: loadingConnection } = useGitConnection();
  const isConnected = !!connection;
  const { data: repo, isLoading: loadingRepo } = useGitSelectedRepo({ enabled: isConnected });
  const { data: branch, isLoading: loadingBranch } = useDefaultGitBranch();

  return {
    orgId,
    isConnected,
    repo: repo ?? null,
    branch: branch ?? null,
    repoFullName: repo?.full_name ?? null,
    branchName: branch?.name ?? null,
    isLoading: loadingConnection || loadingRepo || loadingBranch,
  };
}
